/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useEffect } from 'react';
import PropTypes, { node, string, bool, oneOfType, func } from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import EbisIcon from 'views/atoms/icon/EbisIcon';
import { MANAGE_MMM_EDIT } from 'domain/permissions/contractPermissions';
import mmmNavConfig from 'domain/commonbar/mmmNavConfig';
import useAuth from 'services/custom-hooks/useAuth';
import { getScreenResolveType } from 'domain/utils';
import screenPermissionConfigs from 'domain/permissions/screenPermissionConfigs';
import MenuItem from 'views/molecules/navbar/component/MenuItem';
import PermissionWrapperRPC from 'services/appconfig/PermissionWrapperRPC';

const MenuIcon = ({ svgIcon, fontIcon, active, isIconMenu }) => {
  if (svgIcon) {
    return (
      <span role="button" className="fa-stack has-background">
        <span className={`bk-svg ${svgIcon}`} />
      </span>
    );
  }

  return (
    <EbisIcon
      isIconMenu={isIconMenu}
      iconName={fontIcon}
      active={active}
    />
  );
};

MenuIcon.defaultProps = {
  isIconMenu: true,
  svgIcon: '',
  fontIcon: '',
};

MenuIcon.propTypes = {
  isIconMenu: bool,
  active: bool.isRequired,
  svgIcon: oneOfType([node, string]),
  fontIcon: string,
};

/**
 * Independent MMM sidebar section.
 * Visibility depends only on MANAGE_MMM_EDIT (not ad/all tab permissions).
 * Placed after LPO (ad tab) / user analysis (all tab) in MenuList; appearance follows current tab via existing classes.
 */
const MmmMenuSection = ({
  screenId,
  tab,
  changeHoverItem,
  activeHoverItem,
}) => {
  const { t } = useTranslation();
  const item = mmmNavConfig;
  const [isActive, setActive] = useState(false);
  const [isShowSubActiveMenu, showSubActiveMenu] = useState(false);

  const { isAllowed: hasManageMmm } = useAuth([MANAGE_MMM_EDIT]);

  useEffect(() => {
    const screenIds = item.submenu.map((submenu) => submenu.screenId);
    setActive(screenIds.includes(screenId) || screenId === item.screenId);
  }, [screenId, item.screenId, item.submenu]);

  useEffect(() => {
    const subMenuActive = item.submenu.some(
      (itemSubmenu) => screenId === itemSubmenu.screenId
    );
    showSubActiveMenu(subMenuActive);
  }, [screenId, item.submenu]);

  if (!hasManageMmm) {
    return null;
  }

  const clsMenu = classNames({
    active: isActive,
    subActive: isShowSubActiveMenu,
    'menu-item': true,
    'menu-item--sub': true,
    'border-top-menu': true,
  });

  const handleShowHideSubmenu = () => {
    showSubActiveMenu((prev) => !prev);
  };

  return (
    <li
      className={clsMenu}
      onMouseEnter={() => changeHoverItem(item.title)}
      onMouseLeave={() => changeHoverItem('')}
    >
      <MenuItem
        title={t(item.title)}
        url={item.url}
        type={item.type}
        newTab={item.newTab}
        leftIcon={
          <MenuIcon
            svgIcon={item.svg}
            fontIcon={item.iconClass}
            active={isActive || activeHoverItem}
          />
        }
        onClick={handleShowHideSubmenu}
      />
      <ul className="menu-nav">
        {item.submenu.map((subItem) => (
          <PermissionWrapperRPC
            key={subItem.screenId}
            requiredPermissions={screenPermissionConfigs[subItem.screenId][tab]}
            resolveType={getScreenResolveType(subItem.screenId)}
            screenId={subItem.screenId}
            render={(allowed) => {
              if (!allowed || !hasManageMmm) {
                return null;
              }

              const isSubActive = screenId === subItem.screenId;
              const clsSubmenuItem = classNames({
                'menu-item': true,
                'ebis--disabled': !allowed,
                active: isSubActive,
              });

              return (
                <li className={clsSubmenuItem}>
                  <MenuItem
                    title={t(subItem.title)}
                    url={subItem.url}
                    leftIcon={
                      <MenuIcon
                        svgIcon={subItem.svg}
                        fontIcon={subItem.iconClass}
                        active={isSubActive}
                      />
                    }
                    type={subItem.type}
                    newTab={subItem.newTab}
                  />
                </li>
              );
            }}
          />
        ))}
      </ul>
    </li>
  );
};

MmmMenuSection.propTypes = {
  screenId: PropTypes.string.isRequired,
  tab: PropTypes.string.isRequired,
  changeHoverItem: func.isRequired,
  activeHoverItem: PropTypes.bool.isRequired,
};

export default React.memo(MmmMenuSection);
